import React, { useMemo, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Package, CheckCircle2, ArrowLeft, ArrowRight, AlertTriangle, Image as ImageIcon, Scale, Ruler, Box, XCircle } from 'lucide-react';

/**
 * PalletPrecheckView - Streamlined pre-check for verifying pallets exist
 * 
 * Focus on:
 * - Image (if available) for visual confirmation
 * - Key metrics: weight and dimensions
 * - Quick issue marking for missing/problematic pallets
 */
export default function PalletPrecheckView({
  pallets = [],
  truckDimensions = { width: 2.45, depth: 12, height: 2.6 },
  onComplete,
  onCancel
}) {
  const [index, setIndex] = useState(0);
  const [issues, setIssues] = useState({}); // { [id]: reason }

  const current = pallets[index];

  // Streamlined issue reasons - focus on most common
  const issueReasons = [
    { value: 'missing', label: 'Missing', icon: '❌', color: 'bg-red-500' },
    { value: 'damaged', label: 'Damaged', icon: '💥', color: 'bg-orange-500' },
    { value: 'wrong_product', label: 'Wrong', icon: '🔄', color: 'bg-yellow-500' },
    { value: 'other', label: 'Other', icon: '❓', color: 'bg-gray-500' },
  ];

  // Check if pallet has an image - check all possible field names
  const palletImage = useMemo(() => {
    if (!current) return null;
    const img = current.imageUrl || current.image_url || 
                current.labelImageUrl || current.label_image_url ||
                current.storage_path || current.storagePath || null;
    // Debug log to help diagnose image issues
    if (process.env.NODE_ENV === 'development') {
      console.log('[PalletPrecheck] Pallet image check:', {
        id: current.id,
        name: current.name,
        imageUrl: current.imageUrl?.substring?.(0, 50),
        image_url: current.image_url?.substring?.(0, 50),
        labelImageUrl: current.labelImageUrl?.substring?.(0, 50),
        label_image_url: current.label_image_url?.substring?.(0, 50),
        resolved: img?.substring?.(0, 50),
      });
    }
    return img;
  }, [current]);

  // Auto-detect warnings
  const warnings = useMemo(() => {
    if (!current) return [];
    const flags = [];
    const h = current?.dimensions?.height || 0;
    if (h > (truckDimensions?.height || 2.6)) {
      flags.push({ type: 'height', text: 'Too tall for trailer' });
    }
    if (current?.notToploadable) {
      flags.push({ type: 'topload', text: 'Cannot stack on top' });
    }
    if (current?.attribute === 'dangerous') {
      flags.push({ type: 'dangerous', text: 'Dangerous goods' });
    }
    return flags;
  }, [current, truckDimensions]);

  const setIssue = useCallback((id, reason) => {
    setIssues(prev => ({ ...prev, [id]: reason }));
  }, []);

  const clearIssue = useCallback((id) => {
    setIssues(prev => {
      const cp = { ...prev };
      delete cp[id];
      return cp;
    });
  }, []);

  const goto = useCallback((delta) => {
    setIndex(prev => Math.min(pallets.length - 1, Math.max(0, prev + delta)));
  }, [pallets.length]);

  const total = pallets.length;
  const issueCount = Object.values(issues).filter(Boolean).length;
  const currentIssue = issues[current?.id];

  // Format dimensions for display
  const formatDimensions = (dims) => {
    if (!dims) return '—';
    const w = dims.width || 0;
    const d = dims.depth || 0;
    const h = dims.height || 0;
    // Convert to cm if values are in meters (< 10)
    if (w < 10 && d < 10 && h < 10) {
      return `${(w * 100).toFixed(0)} × ${(d * 100).toFixed(0)} × ${(h * 100).toFixed(0)} cm`;
    }
    return `${w} × ${d} × ${h} cm`;
  };

  if (!current) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No pallets to review</p>
          <Button variant="outline" onClick={() => onCancel?.()}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Compact Header */}
      <div className="bg-white border-b px-3 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="text-sm font-semibold">Pre-check</div>
          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {index + 1} / {total}
          </div>
          {issueCount > 0 && (
            <div className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
              {issueCount} issue{issueCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs h-8">
              Cancel
            </Button>
          )}
          <Button size="sm" onClick={() => onComplete?.(issues)} className="text-xs h-8 bg-green-600 hover:bg-green-700">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            Done
          </Button>
        </div>
      </div>

      {/* Main Content - Optimized for quick visual check */}
      <div className="flex-1 overflow-auto p-3">
        <div className="max-w-lg mx-auto space-y-3">
          
          {/* Pallet Name & Status */}
          <div className="bg-white rounded-lg border p-3">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-2 flex-wrap">
                  <h2 className="font-semibold text-lg truncate">{current.name || `Pallet ${current.id}`}</h2>
                  {current.isImportedPallet && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex-shrink-0">
                      Imported
                    </span>
                  )}
                </div>
                {current.destination && (
                  <p className="text-sm text-blue-600 truncate">→ {current.destination}</p>
                )}
                {current.shipment_id && (
                  <p className="text-xs text-gray-500">ID: {current.shipment_id}</p>
                )}
              </div>
              {currentIssue && (
                <div className="flex items-center space-x-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
                  <XCircle className="h-3 w-3" />
                  <span>{issueReasons.find(r => r.value === currentIssue)?.label || currentIssue}</span>
                </div>
              )}
            </div>
          </div>

          {/* Image or Placeholder */}
          <div className="bg-white rounded-lg border overflow-hidden">
            {palletImage ? (
              <div className="aspect-video bg-gray-100 relative">
                <img 
                  src={palletImage} 
                  alt={current.name || 'Pallet'} 
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden absolute inset-0 items-center justify-center bg-gray-100">
                  <div className="text-center text-gray-400">
                    <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                    <p className="text-xs">Image failed to load</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm font-medium">No image available</p>
                  <p className="text-xs">Verify pallet by ID & dimensions</p>
                </div>
              </div>
            )}
          </div>

          {/* Key Metrics - Weight & Dimensions */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white rounded-lg border p-3">
              <div className="flex items-center space-x-2 text-gray-500 mb-1">
                <Scale className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wide">Weight</span>
              </div>
              <div className="text-xl font-bold">
                {current.weight ? `${current.weight} kg` : '—'}
              </div>
            </div>
            <div className="bg-white rounded-lg border p-3">
              <div className="flex items-center space-x-2 text-gray-500 mb-1">
                <Ruler className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wide">Size</span>
              </div>
              <div className="text-sm font-semibold">
                {formatDimensions(current.dimensions)}
              </div>
            </div>
          </div>

          {/* Additional Info (collapsed) */}
          {(current.attribute && current.attribute !== 'none') || current.notToploadable ? (
            <div className="bg-white rounded-lg border p-3">
              <div className="flex items-center space-x-2 text-gray-500 mb-2">
                <Box className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wide">Properties</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {current.attribute && current.attribute !== 'none' && (
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    current.attribute === 'dangerous' ? 'bg-red-100 text-red-700' :
                    current.attribute === 'food' ? 'bg-green-100 text-green-700' :
                    current.attribute === 'medicine' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {current.attribute}
                  </span>
                )}
                {current.notToploadable && (
                  <span className="text-xs px-2 py-1 rounded-full font-medium bg-amber-100 text-amber-700">
                    No stacking
                  </span>
                )}
              </div>
            </div>
          ) : null}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  {warnings.map((w, i) => (
                    <div key={i}>{w.text}</div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Quick Issue Buttons */}
          <div className="bg-white rounded-lg border p-3">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Mark issue if pallet can't be loaded</div>
            <div className="grid grid-cols-4 gap-2">
              {issueReasons.map(r => {
                const active = currentIssue === r.value;
                return (
                  <button
                    key={r.value}
                    onClick={() => active ? clearIssue(current.id) : setIssue(current.id, r.value)}
                    className={`p-2 rounded-lg border-2 transition-all text-center ${
                      active 
                        ? 'border-red-500 bg-red-50 text-red-700' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-lg block mb-0.5">{r.icon}</span>
                    <span className="text-[10px] font-medium">{r.label}</span>
                  </button>
                );
              })}
            </div>
            {currentIssue && (
              <button 
                onClick={() => clearIssue(current.id)}
                className="w-full mt-2 text-xs text-red-600 hover:text-red-700 py-1"
              >
                Clear issue
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="bg-white border-t px-3 py-2">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goto(-1)}
            disabled={index === 0}
            className="h-10 px-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Prev
          </Button>
          
          {/* Progress dots for mobile */}
          <div className="flex items-center space-x-1">
            {pallets.length <= 10 ? (
              pallets.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === index 
                      ? 'bg-blue-600 w-4' 
                      : issues[p.id] 
                        ? 'bg-red-400' 
                        : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))
            ) : (
              <span className="text-sm text-gray-500">{index + 1} of {total}</span>
            )}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => goto(1)}
            disabled={index >= pallets.length - 1}
            className="h-10 px-4"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
